import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getSupplierCities, getSupplierProductTypes, getSupplierCurrencies, addTourProduct } from '../services/apiService';

// Utility: Hangul initial consonant (초성) extraction for search
const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const HANGUL_BASE = 0xAC00; // 가
const CHOSUNG = 21 * 28;

function getInitials(str) {
  if (!str) return '';
  let res = '';
  for (let ch of String(str)) {
    const code = ch.charCodeAt(0);
    if (code >= 0xAC00 && code <= 0xD7A3) {
      const idx = Math.floor((code - HANGUL_BASE) / CHOSUNG);
      res += CHO[idx] || ch;
    } else {
      res += ch.toUpperCase();
    }
  }
  return res;
}

const NewProductModal = ({ open, onClose, onCreated, fixedProductTypeName = '' }) => {
  const [mounted, setMounted] = useState(false);
  const [animOut, setAnimOut] = useState(false);

  // Form fields
  const [cityOptions, setCityOptions] = useState([]); // [{코드, 코드명}]
  const [cityQuery, setCityQuery] = useState('');
  const [cityCode, setCityCode] = useState('');
  const [cityOpen, setCityOpen] = useState(false);

  const [typeOptions, setTypeOptions] = useState([]); // [{코드, 코드명}]
  const [typeQuery, setTypeQuery] = useState('');
  const [typeCode, setTypeCode] = useState('');
  const [typeOpen, setTypeOpen] = useState(false);

  const [currencyOptions, setCurrencyOptions] = useState([]); // [{코드, 코드명}]
  const [currencyCode, setCurrencyCode] = useState('');

  const [productName, setProductName] = useState('');
  const [durationValue, setDurationValue] = useState('');
  const [durationUnit, setDurationUnit] = useState('일'); // '일' | '시간'

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fixedTypeName = String(fixedProductTypeName || '').trim();

  // Derived filtered city options with 초성검색
  const filteredCities = useMemo(() => {
    const q = (cityQuery || '').trim();
    if (!q) return cityOptions;
    const qUpper = q.toUpperCase();
    const qCho = getInitials(q);

    const rank = (name) => {
      const upper = String(name || '').toUpperCase();
      const nameCho = getInitials(name || '');
      if (upper === qUpper) return 0; // exact phrase match
      if (upper.startsWith(qUpper)) return 10; // prefix phrase match
      if (upper.includes(qUpper)) return 20; // substring phrase match
      if (nameCho === qCho) return 30; // exact initials
      if (nameCho.startsWith(qCho)) return 40; // prefix initials
      if (nameCho.includes(qCho)) return 50; // substring initials
      return 999;
    };

    return (cityOptions || [])
      .filter((opt) => {
        const name = String(opt.코드명 || '');
        const upper = name.toUpperCase();
        const nameCho = getInitials(name);
        // Include if phrase contains OR initials contains
        return upper.includes(qUpper) || nameCho.includes(qCho);
      })
      .sort((a, b) => {
        const ra = rank(a.코드명);
        const rb = rank(b.코드명);
        if (ra !== rb) return ra - rb;
        return String(a.코드명 || '').localeCompare(String(b.코드명 || ''));
      });
  }, [cityQuery, cityOptions]);

  // Derived filtered type options with 초성검색
  const filteredTypes = useMemo(() => {
    const q = (typeQuery || '').trim();
    if (!q) return typeOptions;
    const qUpper = q.toUpperCase();
    const qCho = getInitials(q);

    const rank = (name) => {
      const upper = String(name || '').toUpperCase();
      const nameCho = getInitials(name || '');
      if (upper === qUpper) return 0; // exact phrase match
      if (upper.startsWith(qUpper)) return 10; // prefix phrase match
      if (upper.includes(qUpper)) return 20; // substring phrase match
      if (nameCho === qCho) return 30; // exact initials
      if (nameCho.startsWith(qCho)) return 40; // prefix initials
      if (nameCho.includes(qCho)) return 50; // substring initials
      return 999;
    };

    return (typeOptions || [])
      .filter((opt) => {
        const name = String(opt.코드명 || '');
        const upper = name.toUpperCase();
        const nameCho = getInitials(name);
        // Include if phrase contains OR initials contains
        return upper.includes(qUpper) || nameCho.includes(qCho);
      })
      .sort((a, b) => {
        const ra = rank(a.코드명);
        const rb = rank(b.코드명);
        if (ra !== rb) return ra - rb;
        return String(a.코드명 || '').localeCompare(String(b.코드명 || ''));
      });
  }, [typeQuery, typeOptions]);

  // Safety: if options load after user blurs, re-run analysis once when options are ready
  useEffect(() => {
    if (!open) return;
    if (!productName || !productName.trim()) return;
    if (!Array.isArray(cityOptions) || cityOptions.length === 0) return;
    if (cityCode) return;
    analyzeNameAndSuggest();
  }, [open, productName, cityOptions, cityCode]);

  // Open effect: fetch dropdown data
  useEffect(() => {
    if (!open) return;
    setAnimOut(false);
    setMounted(true);
    setError('');
    setCityOpen(false);
    setTypeOpen(false);
    (async () => {
      try {
        setLoading(true);
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) throw new Error('토큰 정보가 없습니다.');
        const [cities, types, currencies] = await Promise.all([
          getSupplierCities({ companyNo }).catch(() => []),
          getSupplierProductTypes({ companyNo }).catch(() => []),
          getSupplierCurrencies({ companyNo }).catch(() => []),
        ]);
        setCityOptions(Array.isArray(cities) ? cities : []);
        setTypeOptions(Array.isArray(types) ? types : []);
        setCurrencyOptions(Array.isArray(currencies) ? currencies : []);
        // Defaults: keep city empty by default (no auto-select)
        if (Array.isArray(types) && types.length > 0) {
          const fixed = fixedTypeName
            ? types.find(t => String(t?.코드명 || '').trim() === fixedTypeName)
            : null;
          if (fixed && fixed.코드 != null) {
            setTypeCode(String(fixed.코드));
          } else if (types[0]) {
            setTypeCode(String(types[0].코드));
          }
        }
        // Prefer KRW if exists
        const krw = (currencies || []).find(c => String(c.코드명).toUpperCase() === 'KRW');
        setCurrencyCode(String((krw || currencies?.[0] || {}).코드 || ''));
      } catch (e) {
        console.error('초기 데이터 로드 실패', e);
        setError('초기 데이터 로드에 실패했습니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  // Analyze product name to suggest city and duration (called on blur/Enter)
  const analyzeNameAndSuggest = () => {
    const name = (productName || '').trim();
    if (!name) return;

    // Helper: parse duration
    const suggestDuration = () => {
      // Prefer pattern like "N박 M일"
      const nm = name.match(/(\d+)\s*박\s*(\d+)\s*일/);
      if (nm) {
        const days = Number(nm[2]);
        if (!durationValue) {
          setDurationValue(String(days));
          setDurationUnit('일');
        }
        return;
      }
      // Pattern like "M일"
      const d = name.match(/(\d+)\s*일/);
      if (d && !/(박)/.test(name)) {
        const days = Number(d[1]);
        if (!durationValue) {
          setDurationValue(String(days));
          setDurationUnit('일');
        }
        return;
      }
      // Pattern like "H시간"
      const h = name.match(/(\d+)\s*시간/);
      if (h) {
        const hours = Number(h[1]);
        if (!durationValue) {
          setDurationValue(String(hours));
          setDurationUnit('시간');
        }
      }
    };

    // Helper: find best city by robust matching (normalize suffixes, full/initials, token-based, ignore spaces/case)
    const suggestCity = () => {
      if (!Array.isArray(cityOptions) || cityOptions.length === 0) return;
      if (cityCode) return; // don't override existing selection

      const nameRaw = name;
      const nameUpper = nameRaw.toUpperCase();
      const nameNoSpace = nameRaw.replace(/\s+/g, '').toUpperCase();
      const nameInitials = getInitials(nameRaw);
      const nameInitialsNoSpace = nameInitials.replace(/\s+/g, '');

      const normalizeKo = (s) => String(s || '')
        .replace(/[^\p{L}\p{N}]+/gu, '') // remove spaces and punctuation
        .replace(/(시|군|구|도|현)$/g, '') // drop common suffix once
        .toUpperCase();

      const nameTokens = String(nameRaw)
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean);
      const nameTokenSet = new Set(nameTokens.map(t => t.toUpperCase()));
      const nameTokenInitials = nameTokens.map(t => getInitials(t));

      let best = null;
      let bestScore = -1;

      const scoreMatch = (label) => {
        const lab = String(label || '');
        if (!lab) return -1;
        const labUpper = lab.toUpperCase();
        const labNoSpace = labUpper.replace(/\s+/g, '');
        const labInitials = getInitials(lab);
        const labInitialsNoSpace = labInitials.replace(/\s+/g, '');
        const labNorm = normalizeKo(lab);
        const nameNorm = normalizeKo(nameRaw);

        let score = -1;
        // Exact phrase match (with/without spaces)
        if (nameUpper === labUpper || nameNoSpace === labNoSpace) score = Math.max(score, 1000 + lab.length);
        // Substring phrase match
        if (nameUpper.includes(labUpper)) score = Math.max(score, 800 + lab.length);
        if (nameNoSpace.includes(labNoSpace)) score = Math.max(score, 700 + lab.length);
        // Normalized containment (handles suffixes and punctuation)
        if (nameNorm && labNorm) {
          if (nameNorm.includes(labNorm)) score = Math.max(score, 760 + labNorm.length);
          if (labNorm.includes(nameNorm)) score = Math.max(score, 745 + labNorm.length);
        }
        // Token-based: if any token appears inside label or label inside token
        for (const tok of nameTokenSet) {
          if (tok.length < 2) continue; // skip too short
          if (labUpper.includes(tok)) score = Math.max(score, 720 + tok.length);
          if (tok.includes(labUpper)) score = Math.max(score, 710 + labUpper.length);
        }
        // Initials match
        if (nameInitials === labInitials) score = Math.max(score, 600 + lab.length);
        if (nameInitials.startsWith(labInitials)) score = Math.max(score, 550 + lab.length);
        if (nameInitials.includes(labInitials)) score = Math.max(score, 500 + lab.length);
        if (nameInitialsNoSpace.includes(labInitialsNoSpace)) score = Math.max(score, 480 + lab.length);
        // Token initials match
        for (const tokInit of nameTokenInitials) {
          if (!tokInit) continue;
          if (tokInit === labInitials) score = Math.max(score, 605 + lab.length);
          if (tokInit.startsWith(labInitials)) score = Math.max(score, 545 + lab.length);
          if (labInitials.startsWith(tokInit)) score = Math.max(score, 540 + tokInit.length);
          if (tokInit.includes(labInitials) || labInitials.includes(tokInit)) score = Math.max(score, 530 + Math.min(tokInit.length, labInitials.length));
        }
        return score;
      };

      for (const opt of cityOptions) {
        const label = String(opt.코드명 || '');
        const s = scoreMatch(label);
        if (s > bestScore) {
          best = opt;
          bestScore = s;
        }
      }
      if (best && bestScore >= 470) { // slightly lower threshold
        console.debug('[NewProductModal] 자동 도시 감지:', best.코드명, 'score=', bestScore, 'from name:', nameRaw);
        setCityCode(String(best.코드));
      } else {
        // Fallback: try a basic normalized containment pass to pick a reasonable candidate
        const nameNorm = normalizeKo(nameRaw);
        const candidate = cityOptions.find(opt => {
          const labNorm = normalizeKo(opt.코드명);
          return !!labNorm && !!nameNorm && (nameNorm.includes(labNorm) || labNorm.includes(nameNorm));
        });
        if (candidate) {
          console.debug('[NewProductModal] Fallback 도시 감지:', candidate.코드명, 'from name:', nameRaw);
          setCityCode(String(candidate.코드));
        } else {
          console.debug('[NewProductModal] 도시 자동 감지 실패. name=', nameRaw, 'options count=', cityOptions.length);
        }
      }
    };

    try {
      suggestDuration();
      suggestCity();
    } catch (e) {
      // no-op on parsing errors
    }
  };

  if (!open) return null;

  const requestClose = () => {
    setAnimOut(true);
    setCityOpen(false);
    setTypeOpen(false);
    setTimeout(() => {
      onClose && onClose();
      setMounted(false);
    }, 200);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (!productName.trim()) {
        setError('상품명을 입력하세요.');
        return;
      }
      if (!cityCode) { setError('도시를 선택하세요.'); return; }
      if (!typeCode) { setError('상품 타입을 선택하세요.'); return; }
      if (!currencyCode) { setError('통화 코드를 선택하세요.'); return; }
      const dv = String(durationValue).trim();
      if (!dv || isNaN(Number(dv))) { setError('소요일을 숫자로 입력하세요.'); return; }
      const durationUnitCode = durationUnit === '일' ? '012001' : '012002';
      const { companyNo, userNo } = getUserInfo() || {};
      const supplierNo = companyNo; // 동일 값 사용
      setLoading(true);
      const resp = await addTourProduct({
        supplierNo,
        userNo,
        companyNo,
        productName: productName.trim(),
        cityNo: cityCode,
        durationValue: dv,
        durationUnitCode,
        currencyCode,
        productTypeCode: typeCode,
      });
      const row = Array.isArray(resp?.table) ? resp.table[0] : null;
      const productNo = row?.상품번호 || row?.ProductNo;
      if (!productNo) {
        setError('상품 생성 결과를 확인할 수 없습니다.');
        return;
      }
      onCreated && onCreated({ productNo: Number(productNo), created: row });
      requestClose();
    } catch (e) {
      console.error('상품 추가 실패', e);
      setError('상품 추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 ${animOut ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div className={`mx-auto mt-20 w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden transform transition-transform duration-200 ${animOut ? 'scale-95' : 'scale-100'}`}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">새 상품 등록</h3>
          <button onClick={requestClose} className="p-2 rounded-lg hover:bg-gray-50 transition-colors" aria-label="닫기">✕</button>
        </div>
        <div className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 rounded px-3 py-2 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">상품명</label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              onBlur={analyzeNameAndSuggest}
              onKeyDown={(e) => { if (e.key === 'Enter') analyzeNameAndSuggest(); }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 bg-white"
              placeholder="상품명을 입력하세요"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">상품 타입</label>
            <div className="relative">
              {/* Combobox trigger - looks like an input */}
              <button
                type="button"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-left text-sm bg-white pr-9 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500"
                onClick={() => {
                  if (fixedTypeName) return;
                  setTypeOpen((o) => !o);
                }}
                disabled={loading || !!fixedTypeName}
                aria-haspopup="listbox"
                aria-expanded={typeOpen ? 'true' : 'false'}
              >
                {(() => {
                  const current = (typeOptions || []).find(c => String(c.코드) === String(typeCode));
                  if (current) return String(current.코드명);
                  return <span className="text-gray-400">상품 타입을 선택하세요</span>;
                })()}
                {/* Dropdown indicator */}
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>
              {/* Dropdown panel */}
              {typeOpen && !fixedTypeName && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg"> 
                  <div className="p-2 border-b bg-gray-50">
                    <input
                      autoFocus
                      type="text"
                      value={typeQuery}
                      onChange={(e) => setTypeQuery(e.target.value)}
                      placeholder="상품 타입 검색 (초성/이름)"
                      className="w-full border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500"
                    />
                  </div>
                  <ul role="listbox" className="max-h-56 overflow-auto py-1">
                    {filteredTypes.length === 0 && (
                      <li className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다.</li>
                    )}
                    {filteredTypes.map((opt) => (
                      <li
                        key={String(opt.코드)}
                        role="option"
                        aria-selected={String(opt.코드) === String(typeCode) ? 'true' : 'false'}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${String(opt.코드) === String(typeCode) ? 'bg-gray-50' : ''}`}
                        onClick={() => {
                          setTypeCode(String(opt.코드));
                          setTypeOpen(false);
                        }}
                      >
                        {String(opt.코드명)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">도시</label>
            <div className="relative">
              {/* Combobox trigger - looks like an input */}
              <button
                type="button"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-left text-sm bg-white pr-9 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500"
                onClick={() => setCityOpen((o) => !o)}
                disabled={loading}
                aria-haspopup="listbox"
                aria-expanded={cityOpen ? 'true' : 'false'}
              >
                {(() => {
                  const current = (cityOptions || []).find(c => String(c.코드) === String(cityCode));
                  if (current) return String(current.코드명);
                  return <span className="text-gray-400">도시를 선택하세요</span>;
                })()}
                {/* Dropdown indicator */}
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>
              {/* Dropdown panel */}
              {cityOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg"> 
                  <div className="p-2 border-b bg-gray-50">
                    <input
                      autoFocus
                      type="text"
                      value={cityQuery}
                      onChange={(e) => setCityQuery(e.target.value)}
                      placeholder="도시 검색 (초성/이름)"
                      className="w-full border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500"
                    />
                  </div>
                  <ul role="listbox" className="max-h-56 overflow-auto py-1">
                    {filteredCities.length === 0 && (
                      <li className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다.</li>
                    )}
                    {filteredCities.map((opt) => (
                      <li
                        key={String(opt.코드)}
                        role="option"
                        aria-selected={String(opt.코드) === String(cityCode) ? 'true' : 'false'}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${String(opt.코드) === String(cityCode) ? 'bg-gray-50' : ''}`}
                        onClick={() => {
                          setCityCode(String(opt.코드));
                          setCityOpen(false);
                        }}
                      >
                        {String(opt.코드명)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">통화 코드</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 bg-white"
              value={currencyCode}
              onChange={(e) => setCurrencyCode(e.target.value)}
              disabled={loading}
            >
              {(currencyOptions || []).map(opt => (
                <option key={String(opt.코드)} value={String(opt.코드)}>
                  {String(opt.코드명)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">소요일</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                value={durationValue}
                onChange={(e) => setDurationValue(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 bg-white"
                placeholder="숫자"
              />
              <select
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 bg-white"
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value)}
              >
                <option value="일">일</option>
                <option value="시간">시간</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors" onClick={requestClose} disabled={loading}>취소</button>
          <button className="px-4 py-2 rounded-lg bg-gray-700 text-white text-sm hover:bg-gray-800 disabled:opacity-50 transition-colors" onClick={handleSubmit} disabled={loading}>
            {loading ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NewProductModal;
